import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, Alert } from 'react-bootstrap';
import { apiService } from '../../services/api';
import './ChatWidget.scss';

interface ChatWidgetProps {
  eventId?: number;
  eventTitle?: string;
  onChatStarted?: (chatRoom: any) => void;
}

interface ChatRoom {
  id: number;
  name: string;
  content_object: string;
  last_message_time_ago: string;
  unread_count: number;
  has_unread: boolean;
  is_public: boolean;
  last_message?: {
    content: string;
    message_type: string;
    sender_name: string;
  };
}


const ChatWidget: React.FC<ChatWidgetProps> = ({ 
  eventId, 
  eventTitle, 
  onChatStarted 
}) => {
  const [showModal, setShowModal] = useState(false);
  const [recentChats, setRecentChats] = useState<ChatRoom[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBeaconData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.chat.getBeaconData();
      
      if (response.success) {
        setRecentChats(response.recent_chats || []);
      } else {
        setError('Failed to load chat data');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load chat data');
    } finally {
      setLoading(false);
    }
  };


  const handleJoinChat = (chatRoom: ChatRoom) => {
    // Dispatch event to open chat in global widget
    window.dispatchEvent(new CustomEvent('openChatRoom', {
      detail: { chatRoom }
    }));
    setShowModal(false);
  };

  useEffect(() => {
    if (showModal) {
      fetchBeaconData();
    }
  }, [showModal]);

  return (
    <>
      {/* Chat Beacon Button */}
      <div className="chat-beacon">
        <Button
          variant="primary"
          className="beacon-button"
          onClick={() => setShowModal(true)}
          title="Start a conversation"
        >
          <i className="fas fa-comments"></i>
        </Button>
      </div>

      {/* Chat Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-comments me-2"></i>
            Start a Conversation
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {loading && (
            <div className="text-center py-5" style={{ paddingTop: '2rem' }}>
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading...</p>
            </div>
          )}

          {error && (
            <Alert variant="danger" className="mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <>
              {/* Recent Chats Section */}
              {recentChats.length > 0 ? (
                <div className="recent-chats-list">
                  {recentChats.map((chat) => (
                    <div 
                      key={chat.id} 
                      className={`chat-item mb-2 p-3 rounded ${chat.has_unread ? 'unread' : ''}`}
                      onClick={() => handleJoinChat(chat)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {chat.name}
                            {chat.has_unread && (
                              <span className="badge bg-primary ms-2">New</span>
                            )}
                          </h6>
                          <p className="mb-1 text-muted small">
                            {chat.content_object}
                          </p>
                          {chat.last_message && (
                            <p className="mb-0 text-muted small">
                              {chat.last_message.sender_name}: {chat.last_message.content}
                            </p>
                          )}
                        </div>
                        <div className="text-end">
                          <small className="text-muted d-block">
                            {chat.last_message_time_ago}
                          </small>
                          {chat.unread_count > 0 && (
                            <span className="badge bg-primary rounded-pill">
                              {chat.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-comments fa-3x text-muted mb-3"></i>
                  <h5>No conversations yet</h5>
                  <p className="text-muted">
                    Your chat conversations will appear here.
                  </p>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ChatWidget;
